import { useCallback, useEffect, useState } from 'react';

export type ThemePref = 'system' | 'light' | 'dark';
const KEY = 'lc.admin.theme';

/** Light unless this browser picked otherwise. */
function read(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'dark' || v === 'system' ? v : 'light';
  } catch {
    return 'light';
  }
}

function apply(pref: ThemePref) {
  const root = document.documentElement;
  if (pref === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', pref);
}

/** Applies the saved preference before first paint (called from main.tsx). */
export const applySavedTheme = () => apply(read());

/** Cycles light → dark → system; the choice is a per-browser convenience. */
export function useTheme() {
  const [pref, setPref] = useState<ThemePref>(read);
  useEffect(() => {
    apply(pref);
    try {
      if (pref === 'light') localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, pref);
    } catch {
      // storage blocked; the theme still applies for this page view
    }
  }, [pref]);
  const cycle = useCallback(() => setPref((p) => (p === 'light' ? 'dark' : p === 'dark' ? 'system' : 'light')), []);
  return { pref, cycle };
}
